/**
 * SectionNotifications — qué avisa la propiedad y por qué canal.
 *
 * La distinción que esta pantalla tiene que dejar clara es la que más se
 * confunde: aquí se decide el aviso de la PROPIEDAD (qué hecho merece un
 * mensaje y por dónde sale), no el permiso de avisos del navegador o del
 * móvil de quien ha iniciado sesión, que vive en los ajustes personales de
 * cada cuenta. Por eso la nota va antes que la matriz.
 *
 * La matriz se dibuja dos veces a propósito: tabla en escritorio y una
 * tarjeta por aviso en móvil. Encoger la tabla en pantallas pequeñas deja
 * celdas de 20px imposibles de pulsar, así que en móvil cada canal es una
 * fila completa de 44px. Ambas versiones comparten los mismos data-testid
 * porque son mutuamente excluyentes por CSS.
 *
 * Sin color y sin urgencia: el estado es simplemente marcado o sin marcar.
 * Ningún valor viene de una decisión aprobada, así que no hay marca
 * "Aprobado" en esta sección: lo que se ve es un ejemplo de partida.
 */
import React from "react";
import { BellSimple, Check, DeviceMobile, Envelope, WhatsappLogo } from "@phosphor-icons/react";
import {
  Block, Card, Note, Advanced, ReadRow,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2, toggleNotification } from "@/lib/settingsV2Store";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { NOTIFICATION_EVENTS, NOTIFICATION_CHANNELS } from "@/data/settingsV2Demo";

const CHANNEL_ICONS = {
  push: DeviceMobile,
  email: Envelope,
  whatsapp: WhatsappLogo,
};

/** Explicación de cada canal, para el desplegable de detalle. */
const CHANNEL_HELP = {
  push: {
    where: "Aplicación móvil",
    hint: "Llega al móvil de quien tenga la aplicación instalada y haya aceptado los avisos en su dispositivo.",
  },
  email: {
    where: "Correo electrónico",
    hint: "Sale por correo al equipo de la propiedad. En Equipo se afina quién recibe cada aviso.",
  },
  whatsapp: {
    where: "Mensaje directo",
    hint: "Va al número de contacto del equipo. Conviene reservarlo para lo que no puede esperar.",
  },
};

function ChannelIcon({ channelKey, size = 14, className = "" }) {
  const Icon = CHANNEL_ICONS[channelKey] || BellSimple;
  return <Icon size={size} className={className} />;
}

/** Cuadrado marcado/sin marcar, idéntico al de CheckRow. */
function Box({ checked }) {
  return (
    <span
      className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
        checked
          ? "bg-stone-900 border-stone-900 text-white dark:bg-stone-100 dark:border-stone-100 dark:text-stone-900"
          : "border-stone-300 dark:border-stone-700"
      }`}
    >
      {checked && <Check size={12} weight="bold" />}
    </span>
  );
}

export default function SectionNotifications() {
  const { notifications } = useSettingsV2();
  // Solo se monta la variante que se ve: dos tablas ocultas por CSS duplicarían
  // cada control y su data-testid en el DOM.
  const wide = useMediaQuery("(min-width: 768px)");

  const isOn = (eventKey, channelKey) => !!notifications?.[eventKey]?.[channelKey];

  const silent = NOTIFICATION_EVENTS.filter(
    (ev) => !NOTIFICATION_CHANNELS.some((ch) => isOn(ev.key, ch.key)),
  );

  return (
    <div data-testid="section-notifications">
      <Block
        title="Avisos de la propiedad"
        description="Qué hechos generan un aviso y por qué canal sale cada uno."
        testId="notifications-events"
      >
        <Note testId="notifications-note">
          Aquí decides qué avisa la propiedad y por qué canal. No es el permiso de avisos del
          navegador ni del móvil de cada persona: eso lo activa cada uno en los ajustes de su
          cuenta, en su dispositivo. Si aquí un aviso no tiene canal, no sale por ninguna vía,
          aunque todo el equipo tenga los permisos concedidos.
        </Note>

        {/* Escritorio: matriz avisos × canales */}
        {wide && (
        <Card padded={false} testId="notifications-matrix">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-stone-100 dark:border-stone-800">
                  <th
                    scope="col"
                    className="text-left font-normal px-5 py-3 text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400"
                  >
                    Aviso
                  </th>
                  {NOTIFICATION_CHANNELS.map((ch) => (
                    <th key={ch.key} scope="col" className="px-4 py-3 w-32">
                      <span className="flex items-center justify-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400">
                        <ChannelIcon channelKey={ch.key} size={13} className="text-stone-400" />
                        {ch.label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
                {NOTIFICATION_EVENTS.map((ev) => (
                  <tr key={ev.key}>
                    <th scope="row" className="text-left font-normal align-top px-5 py-3">
                      <span className="block text-sm text-stone-900 dark:text-stone-100">
                        {ev.label}
                      </span>
                      <span className="block text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                        {ev.hint}
                      </span>
                    </th>
                    {NOTIFICATION_CHANNELS.map((ch) => {
                      const on = isOn(ev.key, ch.key);
                      return (
                        <td key={ch.key} className="px-2 py-1 align-middle">
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={on}
                            aria-label={`${ev.label} · ${ch.label}`}
                            onClick={() => toggleNotification(ev.key, ch.key)}
                            data-testid={`notif-${ev.key}-${ch.key}`}
                            className="w-full min-h-[44px] flex items-center justify-center rounded-md hover:bg-stone-50 dark:hover:bg-stone-900 transition-colors"
                          >
                            <Box checked={on} />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        )}

        {/* Móvil: una tarjeta por aviso, cada canal en su propia fila */}
        {!wide && (
        <div className="space-y-3" data-testid="notifications-mobile">
          {NOTIFICATION_EVENTS.map((ev) => (
            <Card key={ev.key} padded={false} testId={`notifications-event-${ev.key}`}>
              <div className="px-4 pt-4 pb-3">
                <div className="text-sm text-stone-900 dark:text-stone-100">{ev.label}</div>
                <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{ev.hint}</div>
              </div>
              <div className="border-t border-stone-100 dark:border-stone-800 divide-y divide-stone-100 dark:divide-stone-800">
                {NOTIFICATION_CHANNELS.map((ch) => {
                  const on = isOn(ev.key, ch.key);
                  return (
                    <button
                      key={ch.key}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      aria-label={`${ev.label} · ${ch.label}`}
                      onClick={() => toggleNotification(ev.key, ch.key)}
                      data-testid={`notif-${ev.key}-${ch.key}`}
                      className="w-full text-left flex items-center gap-3 min-h-[44px] px-4 py-2 hover:bg-stone-50 dark:hover:bg-stone-900 transition-colors"
                    >
                      <Box checked={on} />
                      <ChannelIcon channelKey={ch.key} size={14} className="text-stone-400 shrink-0" />
                      <span className="text-sm text-stone-900 dark:text-stone-100">{ch.label}</span>
                      <span className="ml-auto text-[11px] text-stone-400 dark:text-stone-500">
                        {on ? "Activado" : "Sin activar"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
        )}

        <p
          className="mt-3 text-xs text-stone-500 dark:text-stone-400 max-w-2xl"
          data-testid="notifications-coverage"
        >
          {silent.length === 0
            ? "Todos los avisos tienen al menos un canal."
            : `Sin ningún canal: ${silent.map((ev) => ev.label).join(", ")}. Estos hechos ocurrirán sin que nadie reciba nada.`}
        </p>

        <Advanced label="Qué es cada canal" testId="notifications-channels-help">
          <Card>
            {NOTIFICATION_CHANNELS.map((ch) => (
              <ReadRow
                key={ch.key}
                label={ch.label}
                value={CHANNEL_HELP[ch.key]?.where || "—"}
                hint={CHANNEL_HELP[ch.key]?.hint}
                testId={`notifications-channel-${ch.key}`}
              />
            ))}
          </Card>
        </Advanced>
      </Block>
    </div>
  );
}
